import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Button, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaPlus, FaChevronDown } from 'react-icons/fa';
import WorkIcon from '@mui/icons-material/Work';
import { COLORS } from '../../../../../constants/radar';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { placementActivityApi } from '../../../../../services/api/placementActivityApi';
import { useAlert } from '../../../../../contexts/AlertContext';
import { decodeHtml } from '../../../../MyActivities/MyCV/helpers/ProcessTemplate';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import EmptyState from '../../../../common/EmptyState';
import ConfirmationModal from '../../../../common/ConfirmationModal';
import PlacementActivityDrawer from './PlacementActivityDrawer';
import {
  TabToolbar,
  TargetSubGroup,
  TargetSubGroupHeader,
  TimelineWrapper,
  TimelineItem,
  TimelineDot,
  TimelineItemContent,
  TimelineItemHeader,
  ActivityTypeChip,
  ActivityDetail,
  ActivityActions,
  ActivityDate,
} from './styles';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(isoString) {
  if (!isoString) { return ''; }
  const d = new Date(isoString);
  const day   = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year  = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function resolveTarget(activity) {
  if (activity.targetProject) { return activity.targetProject.name; }
  if (activity.targetProjectName) { return activity.targetProjectName; }
  return null;
}

function resolveClient(activity) {
  if (activity.targetClient) { return activity.targetClient.name; }
  if (activity.targetClientName) { return activity.targetClientName; }
  if (activity.targetProject?.client) { return activity.targetProject.client.name; }
  return null;
}

function resolveTargetKey(activity) {
  if (activity.targetProjectId) { return `proj_${activity.targetProjectId}`; }
  if (activity.targetProjectName) { return `custom_${activity.targetProjectName}`; }
  return '__no_target__';
}

/**
 * Two-level grouping:
 *   1. Source project (current employee project)
 *   2. Within each source group → sub-group by target project/name
 *
 * Source groups ordered by most recent activity (newest first).
 * Target sub-groups ordered by most recent activity (newest first).
 * Activities within each target sub-group ordered newest first.
 */
function groupActivities(activities) {
  const sourceMap = new Map();

  activities.forEach((activity) => {
    const sourceName = activity.sourceEmployeeProject?.project?.name || 'Unknown Project';
    if (!sourceMap.has(sourceName)) {
      sourceMap.set(sourceName, { items: [], latestAt: '' });
    }
    const bucket = sourceMap.get(sourceName);
    bucket.items.push(activity);
    if (!bucket.latestAt || activity.createdAt > bucket.latestAt) {
      bucket.latestAt = activity.createdAt;
    }
  });

  const sourceGroups = Array.from(sourceMap.entries()).map(([sourceName, { items, latestAt }]) => {
    // Sub-group by target
    const targetMap = new Map();

    items.forEach((activity) => {
      const key = resolveTargetKey(activity);
      if (!targetMap.has(key)) {
        targetMap.set(key, {
          key,
          targetLabel: resolveTarget(activity),
          clientLabel: resolveClient(activity),
          items: [],
          latestAt: '',
        });
      }
      const tb = targetMap.get(key);
      tb.items.push(activity);
      if (!tb.latestAt || activity.createdAt > tb.latestAt) {
        tb.latestAt = activity.createdAt;
      }
    });

    // Sort items within each target (newest first)
    targetMap.forEach((tb) => {
      tb.items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    });

    // Sort target sub-groups by most recent activity (newest first)
    const targetGroups = Array.from(targetMap.values()).sort(
      (a, b) => new Date(b.latestAt) - new Date(a.latestAt)
    );

    return { sourceName, targetGroups, latestAt };
  });

  // Sort source groups by most recent activity (newest first)
  sourceGroups.sort((a, b) => new Date(b.latestAt) - new Date(a.latestAt));

  return sourceGroups;
}

// ── Component ─────────────────────────────────────────────────────────────────

const PlacementActivity = ({ employeeId }) => {
  const { showAlert } = useAlert();
  // ADR-009: affordances are driven by per-activity `permissions: { canEdit, canDelete }`
  // and a top-level `permissions: { canCreate }` returned by the API. No client-side
  // authorization logic — the server is the source of truth.
  const [activities, setActivities] = useState([]);
  const [canCreate, setCanCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activityToEdit, setActivityToEdit] = useState(null);
  const [activityToDelete, setActivityToDelete] = useState(null);
  const [prefillTarget, setPrefillTarget] = useState(null);

  useEffect(() => {
    if (employeeId) {
      fetchActivities();
    }
  }, [employeeId]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const data = await placementActivityApi.getActivitiesForEmployee(employeeId);
      // Defensive: accept either the ADR-009 envelope { activities, permissions } or the
      // legacy raw array shape (older backends / pre-ADR-009 callers).
      if (Array.isArray(data)) {
        setActivities(data);
        setCanCreate(false);
      } else {
        setActivities(data?.activities || []);
        setCanCreate(Boolean(data?.permissions?.canCreate));
      }
    } catch (err) {
      showAlert(err.message || 'Failed to fetch placement activities', 'error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setPrefillTarget(null);
    setActivityToEdit(null);
    setDrawerOpen(true);
  };

  const handleOpenAddForTarget = (items) => {
    const first = items[0];
    setPrefillTarget({
      ownerId:           first.ownerId           || '',
      targetProjectId:   first.targetProjectId   || '',
      targetProjectName: first.targetProjectName || '',
      targetClientId:    first.targetClientId    || '',
      targetClientName:  first.targetClientName  || '',
      useCustomProject:  !first.targetProjectId  && Boolean(first.targetProjectName),
      useCustomClient:   !first.targetClientId   && Boolean(first.targetClientName),
    });
    setActivityToEdit(null);
    setDrawerOpen(true);
  };

  const handleOpenEdit = (activity) => {
    setActivityToEdit(activity);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setActivityToEdit(null);
    setPrefillTarget(null);
  };

  const handleSaved = () => {
    fetchActivities();
  };

  const handleDeleteConfirm = async () => {
    if (!activityToDelete) { return; }
    try {
      await placementActivityApi.deleteActivity(activityToDelete.id);
      showAlert('Placement activity deleted', 'success');
      setActivityToDelete(null);
      fetchActivities();
    } catch (err) {
      showAlert(err.message || 'Failed to delete placement activity', 'error', err);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const sourceGroups = groupActivities(activities);

  return (
    <Box sx={{ flex: 1, background: 'white', overflowY: 'auto', padding: '8px 24px 0 24px' }}>
      <TabToolbar>
        <Typography variant="body2" sx={{ color: '#64748B' }}>
          <strong>{activities.length}</strong> {activities.length === 1 ? 'activity' : 'activities'} recorded
        </Typography>
        {canCreate && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FaPlus />}
            onClick={handleOpenAdd}
            sx={{
              color: '#1e3a5f',
              borderColor: '#1e3a5f',
              backgroundColor: 'white',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#1e3a5f', color: 'white', borderColor: '#1e3a5f' },
            }}
          >
            Add Activity
          </Button>
        )}
      </TabToolbar>

      {sourceGroups.length === 0 ? (
        <EmptyState
          title="No placement activities yet"
          description={
            canCreate
              ? "Click 'Add Activity' to record the first placement activity for this individual."
              : 'No placement activities have been recorded for this individual.'
          }
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: '8px', paddingBottom: '16px' }}>
        {sourceGroups.map(({ sourceName, targetGroups, latestAt }, index) => {
          const totalItems = targetGroups.reduce((sum, tg) => sum + tg.items.length, 0);
          return (
          <Accordion
            key={sourceName}
            defaultExpanded={index === 0}
            sx={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #e0e0e0',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<FaChevronDown style={{ color: COLORS.PRIMARY }} />}
              sx={{
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #e0e0e0',
                '&.Mui-expanded': { minHeight: '48px' },
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                  '&.Mui-expanded': { margin: '12px 0' },
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WorkIcon sx={{ fontSize: 16, color: COLORS.PRIMARY }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 400, color: '#94A3B8' }}>
                    While assigned to
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLORS.PRIMARY }}>
                    {sourceName}
                  </Typography>
                </Box>
                <Chip
                  label={`${targetGroups.length} ${targetGroups.length === 1 ? 'proposal' : 'proposals'}`}
                  size="small"
                  sx={{ fontSize: '11px', height: '20px', backgroundColor: '#e0e7ff', color: COLORS.PRIMARY }}
                />
                <Typography sx={{ color: '#94A3B8', lineHeight: 1, fontSize: '20px' }}>·</Typography>
                <Chip
                  label={`${totalItems} ${totalItems === 1 ? 'activity' : 'activities'}`}
                  size="small"
                  sx={{ fontSize: '11px', height: '20px', backgroundColor: '#e0e7ff', color: COLORS.PRIMARY }}
                />
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ padding: '16px' }}>
            {targetGroups.map(({ key, targetLabel, clientLabel, items }) => (
              <TargetSubGroup key={key}>
                {targetLabel && (
                  <TargetSubGroupHeader>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <BusinessIcon sx={{ fontSize: 13 }} />
                      <span style={{ color: '#94A3B8', fontWeight: 400, marginRight: '2px' }}>Proposed to</span>
                      {targetLabel}
                      {clientLabel && (
                        <span style={{ color: '#94A3B8', fontWeight: 400 }}> · {clientLabel}</span>
                      )}
                    </Box>
                    {canCreate && (
                      <Button
                        size="small"
                        startIcon={<FaPlus style={{ fontSize: 10 }} />}
                        onClick={() => handleOpenAddForTarget(items)}
                        sx={{
                          fontSize: '12px',
                          color: '#475569',
                          textTransform: 'none',
                          fontWeight: 500,
                          padding: '2px 8px',
                          minWidth: 0,
                          '&:hover': { backgroundColor: '#e2e8f0' },
                        }}
                      >
                        Add Activity
                      </Button>
                    )}
                  </TargetSubGroupHeader>
                )}

                <TimelineWrapper>
                  {items.map((activity, idx) => (
                    <TimelineItem key={activity.id} $last={idx === items.length - 1}>
                      <TimelineDot $color={activity.activityType?.color} />

                      <TimelineItemContent>
                        <TimelineItemHeader>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
                            <ActivityDate>{formatDate(activity.createdAt)}</ActivityDate>
                            {activity.activityType && (
                              <ActivityTypeChip $color={activity.activityType.color}>
                                {activity.activityType.name}
                              </ActivityTypeChip>
                            )}
                            {activity.statusType && (
                              <>
                                <ActivityDate>·</ActivityDate>
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: activity.statusType.color,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {activity.statusType.name}
                                </Box>
                              </>
                            )}
                          </Box>
                          {(activity.permissions?.canEdit || activity.permissions?.canDelete) ? (
                            <ActivityActions>
                              {activity.permissions?.canEdit && (
                                <IconButton size="small" onClick={() => handleOpenEdit(activity)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              {activity.permissions?.canDelete && (
                                <IconButton size="small" color="error" onClick={() => setActivityToDelete(activity)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </ActivityActions>
                          ) : (
                            // ADR-009: muted hint that this card is read-only for the current user.
                            // The owner row below already names the responsible employee.
                            <Box
                              component="span"
                              sx={{
                                fontSize: '11px',
                                color: '#94A3B8',
                                fontStyle: 'italic',
                                whiteSpace: 'nowrap',
                                paddingLeft: '8px',
                              }}
                              title="You can view this activity but cannot edit or delete it. Only the owner or the individual's manager chain can modify it."
                            >
                              Read only
                            </Box>
                          )}
                        </TimelineItemHeader>

                        {activity.owner && (
                          <ActivityDetail>
                            <PersonIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: '3px' }} />
                            {activity.owner.fullName}
                          </ActivityDetail>
                        )}

                        {activity.comments && (
                          <Box
                            sx={{
                              fontSize: '13px',
                              color: '#475569',
                              marginTop: '10px',
                              '& p': { margin: 0 },
                              '& ul, & ol': { margin: '4px 0', paddingLeft: '1.4rem' },
                              '& li': { margin: '2px 0' },
                            }}
                            dangerouslySetInnerHTML={{ __html: decodeHtml(activity.comments) }}
                          />
                        )}
                      </TimelineItemContent>
                    </TimelineItem>
                  ))}
                </TimelineWrapper>
              </TargetSubGroup>
            ))}
            </AccordionDetails>
          </Accordion>
          );
        })}
        </Box>
      )}

      {/*
        ADR-009: the drawer and delete modal are always mounted — the affordances that
        open them are individually gated by per-activity `permissions.canEdit/canDelete`
        and top-level `permissions.canCreate`, so an unauthorized user has no entry point
        to either dialog. Keeping them mounted (and uncontrolled by a coarse flag) lets the
        existing edit and create flows share the same drawer instance.
      */}
      <PlacementActivityDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        employeeId={employeeId}
        activityToEdit={activityToEdit}
        onSaved={handleSaved}
        prefillTarget={prefillTarget}
      />

      <ConfirmationModal
        isOpen={Boolean(activityToDelete)}
        onClose={() => setActivityToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Placement Activity"
        message="Are you sure you want to delete this placement activity? This action cannot be undone."
        actionButtonText="Delete"
        actionButtonColor="#DC2626"
      />
    </Box>
  );
};

export default PlacementActivity;
